import { createSlice, type PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { IExcelState } from '../types';
import { api } from '../api/client';

const initialState: IExcelState = {
    tmpPath: null,
    fileName: null,
    sheetNames: [],
    selectedSheet: null,
    headers: [],
    previewData: [],
    cachedMapping: null,
    autoMapping: {},
    totalDataRows: 0,
    headerRow: 0,
    rowIndices: [],
    imageMap: {},
    uploading: false,
    parsing: false,
    importing: false,
};

export const uploadExcel = createAsyncThunk(
    'excel/upload',
    async ({ file, offerCode }: { file: File; offerCode?: number }, { rejectWithValue }) => {
        try {
            return await api.uploadExcel(file, offerCode);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const parseSheet = createAsyncThunk(
    'excel/parseSheet',
    async ({ tmpPath, sheetName, vendorCode, offerCode }: { tmpPath: string; sheetName: string; vendorCode?: number; offerCode?: number }, { rejectWithValue }) => {
        try {
            return await api.parseSheet(tmpPath, sheetName, vendorCode, offerCode);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const parseAllRows = createAsyncThunk(
    'excel/parseAll',
    async ({ tmpPath, sheetName, offerCode }: { tmpPath: string; sheetName: string; offerCode?: number }, { rejectWithValue }) => {
        try {
            return await api.parseAllRows(tmpPath, sheetName, offerCode);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const saveMappingCache = createAsyncThunk(
    'excel/saveMapping',
    async ({ headers, mapping, vendorCode }: { headers: string[]; mapping: Record<string, number>; vendorCode?: number }, { rejectWithValue }) => {
        try {
            return await api.saveMapping(headers, mapping, vendorCode);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const cleanupExcel = createAsyncThunk(
    'excel/cleanup',
    async (tmpPath: string, { rejectWithValue }) => {
        try {
            return await api.cleanupExcel(tmpPath);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

const excelSlice = createSlice({
    name: 'excel',
    initialState,
    reducers: {
        setSelectedSheet: (state, action: PayloadAction<string>) => {
            state.selectedSheet = action.payload;
        },
        resetExcel: () => initialState,
    },
    extraReducers: (builder) => {
        // upload
        builder
            .addCase(uploadExcel.pending, (state) => { state.uploading = true; })
            .addCase(uploadExcel.fulfilled, (state, action) => {
                state.uploading = false;
                state.tmpPath = action.payload.tmp_path;
                state.fileName = action.payload.file_name;
                state.sheetNames = action.payload.sheet_names;
            })
            .addCase(uploadExcel.rejected, (state) => { state.uploading = false; });

        // parseSheet
        builder
            .addCase(parseSheet.pending, (state) => { state.parsing = true; })
            .addCase(parseSheet.fulfilled, (state, action) => {
                state.parsing = false;
                state.headers = action.payload.headers;
                state.previewData = action.payload.data;
                state.cachedMapping = action.payload.cached_mapping;
                state.autoMapping = action.payload.auto_mapping;
                state.totalDataRows = action.payload.total_data_rows;
                state.headerRow = action.payload.header_row ?? 0;
                state.rowIndices = (action.payload as any).row_indices ?? [];
                state.imageMap = action.payload.image_map ?? {};
            })
            .addCase(parseSheet.rejected, (state) => { state.parsing = false; });

        // parseAll
        builder
            .addCase(parseAllRows.pending, (state) => { state.importing = true; })
            .addCase(parseAllRows.fulfilled, (state) => { state.importing = false; })
            .addCase(parseAllRows.rejected, (state) => { state.importing = false; });

        // cleanup
        builder
            .addCase(cleanupExcel.fulfilled, () => initialState);
    },
});

export const { setSelectedSheet, resetExcel } = excelSlice.actions;
export default excelSlice.reducer;
